import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Alert,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Snackbar,
  Badge
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GroupsIcon from '@mui/icons-material/Groups';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { useLanguage } from '../contexts/LanguageContext';
import { encodeState, clearFromLocalStorage } from '../utils/stateManager';
import { normalizeMexicanoPairing } from '../utils/mexicano';
import { loadTeam, saveTeam, teamExists, getCachedTeams, suggestTeamName, normalizeTeamKey, verifyPin, isValidPin, teamHasPin } from '../utils/teamStorage';
import { getTeamSuffixes } from '../translations/teamSuffixes';
import TopActionBar from './TopActionBar';

const createEmptyPlayer = (gender = 'male') => ({ name: '', gender });

function PlayerSetup() {
  const { name: tournamentName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { translate, language } = useLanguage();
  const [players, setPlayers] = useState([
    createEmptyPlayer('male'),
    createEmptyPlayer('female'),
    createEmptyPlayer('male'),
    createEmptyPlayer('female')
  ]); // Start with 4 empty fields
  const [courts, setCourts] = useState(1);
  const [errors, setErrors] = useState({});
  const [isMixedPairs, setIsMixedPairs] = useState(false);
  const playerInputRefs = useRef([]);
  const pendingFocusIndexRef = useRef(null);

  // Saved teams (reusable player lists)
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [loadNameInput, setLoadNameInput] = useState('');
  const [saveNameInput, setSaveNameInput] = useState('');
  const [teamDialogError, setTeamDialogError] = useState('');
  const [teamBusy, setTeamBusy] = useState(false);
  const [suggestedName, setSuggestedName] = useState('');
  const [cachedTeams, setCachedTeams] = useState([]);
  const [snackbar, setSnackbar] = useState('');
  // Name of the team currently loaded/saved, so saving under the same name updates it.
  const [loadedTeamName, setLoadedTeamName] = useState('');
  // PIN handling
  const [loadPin, setLoadPin] = useState('');
  const [savePin, setSavePin] = useState('');
  const [awaitingPin, setAwaitingPin] = useState(false);
  // "New" badge on the team buttons until the user first opens one.
  const [teamsFeatureSeen, setTeamsFeatureSeen] = useState(() => {
    try {
      return localStorage.getItem('vamos-tamos-teams-feature-seen') === '1';
    } catch (e) {
      return true;
    }
  });

  const markTeamsFeatureSeen = () => {
    if (teamsFeatureSeen) return;
    try {
      localStorage.setItem('vamos-tamos-teams-feature-seen', '1');
    } catch (e) {
      // ignore write failures
    }
    setTeamsFeatureSeen(true);
  };

  const { format, roundType, targetPoints, roundMinutes, mexicanoPairing } = React.useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const fromUrl = (searchParams.get('format') || '').toLowerCase();
    const parsedRoundType = (searchParams.get('roundType') || 'points').toLowerCase() === 'time' ? 'time' : 'points';
    const parsedTargetPoints = Number(searchParams.get('targetPoints')) || 21;
    const parsedRoundMinutes = Number(searchParams.get('roundMinutes')) || 12;
    return {
      format: fromUrl === 'mexicano' ? 'mexicano' : 'americano',
      roundType: parsedRoundType,
      targetPoints: parsedTargetPoints,
      roundMinutes: parsedRoundMinutes,
      mexicanoPairing: normalizeMexicanoPairing(searchParams.get('mexicanoPairing'))
    };
  }, [location.search]);

  const handleMixedPairsToggle = (checked) => {
    setIsMixedPairs(checked);
    if (!checked) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.genderBalance;
        return next;
      });
      return;
    }

    setPlayers((prev) => {
      let updated = prev.map((player, idx) => ({
        ...player,
        gender: idx % 2 === 0 ? 'male' : 'female'
      }));

      if (updated.length % 2 !== 0) {
        const nextGender = updated.length % 2 === 0 ? 'male' : 'female';
        updated = [...updated, createEmptyPlayer(nextGender)];
      }

      return updated;
    });
  };

  const handlePlayerChange = (index, value) => {
    setPlayers((prev) => {
      const newPlayers = [...prev];
      newPlayers[index] = { ...newPlayers[index], name: value };

      // Mobile-friendly: if the user starts filling the last field,
      // automatically append a new empty input below (no button tap needed).
      if (index === prev.length - 1 && value.trim()) {
        if (isMixedPairs) {
          newPlayers.push(createEmptyPlayer('male'), createEmptyPlayer('female'));
        } else {
          newPlayers.push(createEmptyPlayer());
        }
      }

      return newPlayers;
    });
    
    // Clear error for this index
    if (errors[`player${index}`]) {
      const newErrors = {...errors};
      delete newErrors[`player${index}`];
      setErrors(newErrors);
    }
  };

  const addPlayerAndFocus = (focusIndex) => {
    pendingFocusIndexRef.current = focusIndex;
    setPlayers((prev) => {
      if (isMixedPairs) {
        return [...prev, createEmptyPlayer('male'), createEmptyPlayer('female')];
      }
      return [...prev, createEmptyPlayer()];
    });
  };

  useEffect(() => {
    const idx = pendingFocusIndexRef.current;
    if (typeof idx !== 'number') return;
    pendingFocusIndexRef.current = null;
    // Defer focus until after the new input mounts.
    setTimeout(() => {
      const el = playerInputRefs.current[idx];
      if (el && typeof el.focus === 'function') el.focus();
      if (el && typeof el.select === 'function') el.select();
    }, 0);
  }, [players.length]);

  const removePlayer = (index) => {
    if (players.length > 4) {
      const newPlayers = players.filter((_, i) => i !== index);
      setPlayers(newPlayers);
    }
  };

  const handleGenderChange = (index, gender) => {
    setPlayers((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], gender };
      return next;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    const normalizedToIndices = new Map();
    const nonEmptyPlayers = [];

    players.forEach((player, index) => {
      const trimmed = player.name.trim();
      if (!trimmed) return;
      nonEmptyPlayers.push({ ...player, name: trimmed });
      const normalized = trimmed.toLowerCase();
      const indices = normalizedToIndices.get(normalized) || [];
      indices.push(index);
      normalizedToIndices.set(normalized, indices);
    });

    // Ignore empty fields; only require enough non-empty names for the selected courts
    if (nonEmptyPlayers.length < courts * 4) {
      newErrors.courts = translate('playerSetup.notEnoughPlayers', {
        count: courts * 4,
        courts: courts
      });
    }

    // Duplicate name check (case-insensitive, trimmed). Ignore empty fields.
    for (const [, indices] of normalizedToIndices.entries()) {
      if (indices.length > 1) {
        indices.forEach((idx) => {
          newErrors[`player${idx}`] = translate('playerSetup.duplicateName');
        });
      }
    }

    if (isMixedPairs) {
      const maleCount = nonEmptyPlayers.filter((p) => p.gender === 'male').length;
      const femaleCount = nonEmptyPlayers.filter((p) => p.gender === 'female').length;
      if (maleCount !== femaleCount) {
        newErrors.genderBalance = translate('playerSetup.genderBalance');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePlayerKeyDown = (index) => (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const nextIndex = index + 1;
    if (nextIndex >= players.length) {
      addPlayerAndFocus(nextIndex);
      return;
    }
    const next = playerInputRefs.current[nextIndex];
    if (next && typeof next.focus === 'function') next.focus();
    if (next && typeof next.select === 'function') next.select();
  };


  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const filteredPlayers = players
        .map((p) => ({ ...p, name: p.name.trim() }))
        .filter((p) => p.name);
      const playerNames = filteredPlayers.map((p) => p.name);
      const playerGenders = filteredPlayers.reduce((acc, player) => {
        acc[player.name] = player.gender;
        return acc;
      }, {});
      const initialState = {
        name: tournamentName,
        players: playerNames,
        playerGenders,
        mixedPairs: isMixedPairs,
        courts,
        format,
        mexicanoPairing,
        roundType,
        targetPoints,
        roundMinutes,
        round: 1,
        scores: playerNames.reduce((acc, p) => ({ ...acc, [p]: 0 }), {}),
        playCount: playerNames.reduce((acc, p) => ({ ...acc, [p]: 0 }), {})
      };

      // Clear any old localStorage state for this tournament before starting fresh
      clearFromLocalStorage(tournamentName);
      
      // Encode state for URL
      const encodedState = encodeState(initialState);
      const query = new URLSearchParams({ state: encodedState });
      navigate(`/tournament/${encodeURIComponent(tournamentName)}?${query}`);
    }
  };

  const openLoadDialog = () => {
    markTeamsFeatureSeen();
    setLoadNameInput('');
    setLoadPin('');
    setTeamDialogError('');
    setCachedTeams(getCachedTeams());
    setLoadDialogOpen(true);
  };

  const openSaveDialog = () => {
    markTeamsFeatureSeen();
    setTeamDialogError('');
    setSuggestedName('');
    setAwaitingPin(false);
    setSavePin('');
    setSaveNameInput((prev) => prev || loadedTeamName || tournamentName || '');
    setSaveDialogOpen(true);
  };

  const closeSaveDialog = () => {
    setSaveDialogOpen(false);
    setAwaitingPin(false);
    setSavePin('');
    setSuggestedName('');
    setTeamDialogError('');
  };

  const applyLoadedTeam = (team) => {
    const loaded = Array.isArray(team?.players) ? team.players : [];
    if (loaded.length === 0) {
      setTeamDialogError(translate('playerSetup.teamNotFound'));
      return false;
    }

    const padded = loaded.map((p) => ({ name: p.name, gender: p.gender }));
    while (padded.length < 4) {
      padded.push(createEmptyPlayer(padded.length % 2 === 0 ? 'male' : 'female'));
    }
    setPlayers(padded);

    // Only enable mixed pairs if the team was explicitly saved that way.
    // Teams saved before this flag existed default to non-mixed.
    setIsMixedPairs(team.mixed === true);

    setErrors({});
    return true;
  };

  const handleLoadTeam = async (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) {
      setTeamDialogError(translate('playerSetup.teamNameRequired'));
      return;
    }
    setTeamBusy(true);
    setTeamDialogError('');
    try {
      const team = await loadTeam(trimmed);
      if (!team) {
        setTeamDialogError(translate('playerSetup.teamNotFound'));
        return;
      }
      // Verify PIN if the team is protected.
      if (teamHasPin(team)) {
        if (!isValidPin(loadPin)) {
          setTeamDialogError(translate('playerSetup.pinEnterPrompt'));
          return;
        }
        const ok = await verifyPin(loadPin, trimmed, team);
        if (!ok) {
          setTeamDialogError(translate('playerSetup.pinIncorrect'));
          return;
        }
      }
      if (applyLoadedTeam(team)) {
        setLoadedTeamName(team.displayName || trimmed);
        setSaveNameInput(team.displayName || trimmed);
        setLoadDialogOpen(false);
        setLoadPin('');
        setSnackbar(translate('playerSetup.teamLoaded'));
      }
    } finally {
      setTeamBusy(false);
    }
  };

  const showTeamSaveError = async (result, name) => {
    if (result.error === 'name-taken') {
      setAwaitingPin(false);
      setSavePin('');
      setLoadedTeamName('');
      setTeamDialogError(translate('playerSetup.teamNameTaken'));
      setSuggestedName(await suggestTeamName(name, getTeamSuffixes(language)));
    } else {
      setTeamDialogError(translate('playerSetup.teamSaveFailed'));
    }
  };

  const handleSaveTeam = async () => {
    const trimmed = saveNameInput.trim();
    if (!trimmed) {
      setTeamDialogError(translate('playerSetup.teamNameRequired'));
      return;
    }
    const playersToSave = players
      .map((p) => ({ name: p.name.trim(), gender: p.gender }))
      .filter((p) => p.name);
    if (playersToSave.length === 0) {
      setTeamDialogError(translate('playerSetup.noPlayersToSave'));
      return;
    }

    setTeamBusy(true);
    try {
      // Saving under the name of the team you loaded/saved updates it (PIN kept).
      const isUpdatingLoadedTeam =
        !!loadedTeamName && normalizeTeamKey(trimmed) === normalizeTeamKey(loadedTeamName);

      if (isUpdatingLoadedTeam) {
        const result = await saveTeam(trimmed, playersToSave, null, isMixedPairs);
        if (result.success) {
          setLoadedTeamName(trimmed);
          setSaveDialogOpen(false);
          setSuggestedName('');
          setTeamDialogError('');
          setSnackbar(translate(result.synced ? 'playerSetup.teamSaved' : 'playerSetup.teamSavedLocally'));
        } else {
          await showTeamSaveError(result, trimmed);
        }
        return;
      }

      // New team: block taken names (suggest one), otherwise go to the PIN step.
      const exists = await teamExists(trimmed);
      if (exists) {
        const suggestion = await suggestTeamName(trimmed, getTeamSuffixes(language));
        setSuggestedName(suggestion);
        setTeamDialogError(translate('playerSetup.teamNameTaken'));
        return;
      }

      setAwaitingPin(true);
      setSavePin('');
      setSuggestedName('');
      setTeamDialogError('');
    } catch (error) {
      setTeamDialogError(translate('playerSetup.teamSaveFailed'));
    } finally {
      setTeamBusy(false);
    }
  };

  const handleConfirmPin = async () => {
    const trimmed = saveNameInput.trim();
    if (!isValidPin(savePin)) {
      setTeamDialogError(translate('playerSetup.pinInvalid'));
      return;
    }
    const playersToSave = players
      .map((p) => ({ name: p.name.trim(), gender: p.gender }))
      .filter((p) => p.name);
    if (playersToSave.length === 0) {
      setTeamDialogError(translate('playerSetup.noPlayersToSave'));
      return;
    }
    setTeamBusy(true);
    try {
      const result = await saveTeam(trimmed, playersToSave, savePin, isMixedPairs, { createOnly: true });
      if (result.success) {
        setLoadedTeamName(trimmed);
        setSaveDialogOpen(false);
        setAwaitingPin(false);
        setSavePin('');
        setTeamDialogError('');
        setSnackbar(translate(result.synced ? 'playerSetup.teamSaved' : 'playerSetup.teamSavedLocally'));
      } else {
        await showTeamSaveError(result, trimmed);
      }
    } catch (error) {
      setTeamDialogError(translate('playerSetup.teamSaveFailed'));
    } finally {
      setTeamBusy(false);
    }
  };

  return (
    <Box>
      <TopActionBar />
      <Typography variant="h4" gutterBottom sx={{
        fontSize: { xs: '1.5rem', sm: '2.125rem' },
        mb: 4,
        color: 'primary.main'
      }}>
        {translate('playerSetup.title')}
      </Typography>

      <Paper sx={{ p: { xs: 3, sm: 4 } }}>
        <form onSubmit={handleSubmit}>
          <Stack
            direction="row"
            spacing={1.5}
            sx={{ mb: 3 }}
          >
            <Badge
              color="error"
              badgeContent={translate('whatsNew.badge')}
              invisible={teamsFeatureSeen}
              sx={{ flex: 1, '& .MuiBadge-badge': { right: 14, top: 6 } }}
            >
              <Button
                variant="outlined"
                startIcon={<GroupsIcon />}
                onClick={openLoadDialog}
                fullWidth
                sx={{ py: 1.25 }}
              >
                {translate('playerSetup.loadTeam')}
              </Button>
            </Badge>
            <Badge
              color="error"
              badgeContent={translate('whatsNew.badge')}
              invisible={teamsFeatureSeen}
              sx={{ flex: 1, '& .MuiBadge-badge': { right: 14, top: 6 } }}
            >
              <Button
                variant="outlined"
                startIcon={<SaveOutlinedIcon />}
                onClick={openSaveDialog}
                fullWidth
                sx={{ py: 1.25 }}
              >
                {translate('playerSetup.saveTeam')}
              </Button>
            </Badge>
          </Stack>

          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'flex-start' }}>
            <FormControlLabel
              control={(
                <Switch
                  checked={isMixedPairs}
                  onChange={(e) => handleMixedPairsToggle(e.target.checked)}
                  color="primary"
                />
              )}
              label={translate('playerSetup.mixedPairsLabel')}
            />
          </Box>

          <Box sx={{ mb: 4 }}>
            {players.map((player, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label={`${translate('playerSetup.playerName')} ${index + 1}`}
                  value={player.name}
                  onChange={(e) => handlePlayerChange(index, e.target.value)}
                  onKeyDown={handlePlayerKeyDown(index)}
                  error={!!errors[`player${index}`]}
                  helperText={errors[`player${index}`]}
                  inputRef={(el) => {
                    playerInputRefs.current[index] = el;
                  }}
                  size="medium"
                  sx={{ flex: 1 }}
                />
                {isMixedPairs && (
                  <ToggleButtonGroup
                    color="primary"
                    exclusive
                    value={player.gender || 'male'}
                    onChange={(_, value) => value && handleGenderChange(index, value)}
                    sx={{
                      height: '56px',
                      alignSelf: 'stretch'
                    }}
                  >
                    <ToggleButton value="male" aria-label={translate('playerSetup.male')}>
                      <span role="img" aria-hidden="true" style={{ fontSize: '1.5rem' }}>
                        ♂
                      </span>
                    </ToggleButton>
                    <ToggleButton value="female" aria-label={translate('playerSetup.female')}>
                      <span role="img" aria-hidden="true" style={{ fontSize: '1.5rem' }}>
                        ♀
                      </span>
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
                {players.length > 4 && (
                  <IconButton
                    onClick={() => removePlayer(index)}
                    sx={{ width: 48, height: 48 }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>

          <Button
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => addPlayerAndFocus(players.length)}
            sx={{ mb: 4, py: 1.5 }}
            fullWidth
          >
            {translate('playerSetup.addPlayer')}
          </Button>

          {errors.genderBalance && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.genderBalance}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 4 }}>
            <InputLabel>{translate('playerSetup.courtsTitle')}</InputLabel>
            <Select
              value={courts}
              label={translate('playerSetup.courtsTitle')}
              onChange={(e) => setCourts(e.target.value)}
            >
              {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
                <MenuItem key={num} value={num}>
                  {num} {num === 1 ? 
                    translate('playerSetup.court') : 
                    translate('playerSetup.courts')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {errors.courts && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.courts}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            sx={{ py: 1.5, fontSize: '1.1rem' }}
          >
            {translate('playerSetup.startTournament')}
          </Button>
        </form>
      </Paper>

      <Dialog
        open={loadDialogOpen}
        onClose={() => setLoadDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{translate('playerSetup.loadTeamTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {translate('playerSetup.loadTeamHint')}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label={translate('playerSetup.teamCode')}
            value={loadNameInput}
            onChange={(e) => {
              setLoadNameInput(e.target.value);
              setTeamDialogError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleLoadTeam(loadNameInput);
              }
            }}
            error={!!teamDialogError}
          />
          <TextField
            fullWidth
            label={translate('playerSetup.pinFourDigits')}
            value={loadPin}
            onChange={(e) => {
              setLoadPin(e.target.value.replace(/\D/g, '').slice(0, 4));
              setTeamDialogError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleLoadTeam(loadNameInput);
              }
            }}
            inputProps={{ inputMode: 'numeric', maxLength: 4 }}
            error={!!teamDialogError}
            helperText={teamDialogError}
            sx={{ mt: 2 }}
          />

          {cachedTeams.length > 0 ? (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                {translate('playerSetup.savedTeamsTitle')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {cachedTeams.map((team) => (
                  <Chip
                    key={team.displayName}
                    label={team.displayName}
                    onClick={() => {
                      setLoadNameInput(team.displayName);
                      setTeamDialogError('');
                    }}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              {translate('playerSetup.noSavedTeams')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoadDialogOpen(false)}>
            {translate('common.cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={teamBusy}
            onClick={() => handleLoadTeam(loadNameInput)}
          >
            {translate('playerSetup.load')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={saveDialogOpen}
        onClose={closeSaveDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{translate('playerSetup.saveTeamTitle')}</DialogTitle>
        <DialogContent>
          {!awaitingPin ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {translate('playerSetup.saveTeamHint')}
              </Typography>
              <TextField
                autoFocus
                fullWidth
                label={translate('playerSetup.teamCode')}
                value={saveNameInput}
                onChange={(e) => {
                  setSaveNameInput(e.target.value);
                  setTeamDialogError('');
                  setSuggestedName('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSaveTeam();
                  }
                }}
                error={!!teamDialogError}
              />
              {!teamDialogError && loadedTeamName &&
                normalizeTeamKey(saveNameInput) === normalizeTeamKey(loadedTeamName) && (
                <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1 }}>
                  {translate('playerSetup.updatingTeam', { name: loadedTeamName })}
                </Typography>
              )}
              {teamDialogError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {teamDialogError}
                </Alert>
              )}
              {suggestedName && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    {translate('playerSetup.suggestionLabel')}
                  </Typography>
                  <Chip
                    color="primary"
                    variant="outlined"
                    label={suggestedName}
                    onClick={() => {
                      setSaveNameInput(suggestedName);
                      setSuggestedName('');
                      setTeamDialogError('');
                    }}
                  />
                </Box>
              )}
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {translate('playerSetup.pinEnterPrompt')}
              </Typography>
              <TextField
                autoFocus
                fullWidth
                label={translate('playerSetup.pinFourDigits')}
                value={savePin}
                onChange={(e) => {
                  setSavePin(e.target.value.replace(/\D/g, '').slice(0, 4));
                  setTeamDialogError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleConfirmPin();
                  }
                }}
                inputProps={{ inputMode: 'numeric', maxLength: 4 }}
                error={!!teamDialogError}
              />
              <Alert severity="warning" sx={{ mt: 2 }}>
                {translate('playerSetup.pinCreateWarning')}
              </Alert>
              {teamDialogError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {teamDialogError}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {awaitingPin ? (
            <>
              <Button
                onClick={() => {
                  setAwaitingPin(false);
                  setSavePin('');
                  setTeamDialogError('');
                }}
              >
                {translate('common.back')}
              </Button>
              <Button
                variant="contained"
                disabled={teamBusy}
                onClick={handleConfirmPin}
              >
                {translate('playerSetup.createTeam')}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={closeSaveDialog}>
                {translate('common.cancel')}
              </Button>
              <Button
                variant="contained"
                disabled={teamBusy}
                onClick={handleSaveTeam}
              >
                {translate('common.save')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar('')}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

export default PlayerSetup;
